import React, {useEffect, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import {useSelector} from 'react-redux';
import {Link} from 'react-router';
import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';
import {getTaskCore} from 'sweet-taskinfo';

import {useAddComment, useComments} from 'app/comments/api';
import {CommentForm} from 'app/comments/components/CommentForm';
import {CommentItem} from 'app/comments/components/CommentItem';
import {CurrentUserSelector} from 'app/current-user/api';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {PopoverForm} from 'app/general/overlays';
import {ProcedureStepNumber} from 'app/procedures/components/ProcedureStepNumber';
import {StyledNumberContainerDiv} from 'app/procedures/components/ProcedureStepStructure';
import {useTaskInstance} from 'app/tasks/api';
import {TaskStepActionButtons} from 'app/tasks/components/ProcedureTaskPage/TaskStepActionButtons';
import {simpleGetProgressLabel} from 'app/tasks/utils';
import {getStepName} from 'app/utils/fallbacks';
import {gt, ngt} from 'app/utils/i18n';

import {PercentageBadge} from './TaskPercentageBadge';

import {CommentSerializedType, ListResponseType} from 'sweet-types';

import type {OverlayProps} from 'app/general/overlays';
import type {NodeType} from 'sweet-graphs';
import type {
  ProcedureTaskCore,
  ProcessTaskCore,
  TaskProgressInfo,
  TaskState,
} from 'sweet-taskinfo';
import type {SlimTaskInstanceSerializedType} from 'sweet-types';

interface Props {
  task: SlimTaskInstanceSerializedType;
  isOverdue: boolean;
  isHovered: boolean;
  overlayProps: OverlayProps;
}

const PREVIEW_DELAY_MS = 650;
const HIDE_DELAY_MS = 50;

export function TaskInstancePreviewPopover({
  task,
  isOverdue,
  isHovered,
  overlayProps,
}: Props) {
  const [info, setInfo] = useState<TaskProgressInfo | undefined>(undefined);
  const [state, setState] = useState<TaskState | undefined>(undefined);

  const {data: instance} = useTaskInstance(task.id ?? skipToken, {
    skip: !overlayProps.isOpen && !isHovered,
    refetchOnMountOrArgChange: false,
  });

  const {data: comments} = useComments(
    task?.id
      ? {
          object_pk: task.id,
          content_type: 'taskinstance',
          exclude_deleted_comments: '1',
          page_size: 3,
        }
      : skipToken,
    {
      skip: !overlayProps.isOpen,
      refetchOnMountOrArgChange: false,
    }
  );

  useEffect(() => {
    if (instance) {
      const core: ProcedureTaskCore | ProcessTaskCore = getTaskCore(instance);
      if (core.hasVersion) {
        setState(core.state);
        setInfo(core.state.progressInfo);
      }
    }
  }, [instance]);

  return (
    <StyledPopoverForm
      overlayProps={{
        ...overlayProps,
        placement: 'top',
        offset: '0, 30',
        delay: {show: PREVIEW_DELAY_MS, hide: HIDE_DELAY_MS},
      }}
    >
      {() => (
        <PopoverBody className="p-0">
          <TaskHeader task={task} info={info} isOverdue={isOverdue} />
          <TaskDetails task={task} state={state} />
          <TaskComments task={task} comments={comments} />
        </PopoverBody>
      )}
    </StyledPopoverForm>
  );
}

interface TaskCommentsProps {
  task: SlimTaskInstanceSerializedType;
  comments?: ListResponseType<CommentSerializedType>;
}

function TaskComments({task, comments}: TaskCommentsProps) {
  const [addComment] = useAddComment();
  function onAddComment(comment) {
    return addComment({
      object_pk: task.id ?? 0,
      parentId: task.id ?? 0,
      content_type: 'taskinstance',
      comment: comment,
    });
  }
  return (
    <BootstrapMinMediumMediaQuery>
      <div className={cs('bg-very-light border-top p-3')}>
        <h5 className="d-flex justify-content-between align-items-center">
          <span>{gt('Comments')}</span>
          {comments && comments.num_pages > 1 ? (
            <Link to={task.comments_url}>
              <small>{gt('View All Comments')}</small>
            </Link>
          ) : null}
        </h5>
        {comments == null && <LoadingText />}
        <ul className="list-unstyled">
          {comments?.results.map((comment) => (
            <CommentItem
              key={comment.url}
              comment={comment}
              isTarget={false}
              canDelete={false}
            />
          ))}
        </ul>
        <CommentForm
          className="d-flex justify-content-between"
          buttonClass="btn-primary"
          onSubmit={onAddComment}
          contentType={task.template.assigned_object.content_type}
          objectId={task.template.assigned_object.id}
        />
      </div>
    </BootstrapMinMediumMediaQuery>
  );
}

interface TaskHeaderProps {
  task: SlimTaskInstanceSerializedType;
  info?: TaskProgressInfo;
  isOverdue: boolean;
}

function TaskHeader({task, isOverdue, info}: TaskHeaderProps) {
  const {style, text} = simpleGetProgressLabel(task);
  const numCompleted = info?.completedPath.length ?? 0;
  const numSteps = info?.isSequenced
    ? info?.farthestEndingDistance
    : info?.activeNodes.size;

  return (
    <div className="p-3">
      <h5>{gt('Progress')}</h5>
      <div className="d-flex align-items-center justify-content-between">
        <span className={cs('badge mr-auto', `badge-${style}`)}>{text}</span>
        <span className="text-nowrap mr-2 ml-2">
          {ngt(
            '%2$d of %1$d step completed',
            '%2$d of %1$d steps completed',
            numSteps ?? 0,
            numCompleted
          )}
        </span>
        <PercentageBadge task={task} isOverdue={isOverdue} size="2rem" />
      </div>
    </div>
  );
}

interface TaskDetailProps {
  task: SlimTaskInstanceSerializedType;
  state?: TaskState;
}

function TaskDetails({task, state}: TaskDetailProps) {
  if (state == null) {
    return <LoadingText />;
  }
  const {completedPath} = state.progressInfo;

  const currentStepId = completedPath[completedPath.length - 1] ?? 'start';
  const step = state.nodeMap[currentStepId] ?? null;

  const [prev, next] = state.getPrevAndNextSteps(step?.body?.id ?? 'start');

  const isEmpty = state.orderedIds.length === 0;

  return (
    <StyledPreviewStepDiv className="p-3">
      <PreviewStep
        task={task}
        step={prev}
        completedPath={completedPath}
        state={state}
      />
      <PreviewStep
        task={task}
        step={step}
        completedPath={completedPath}
        state={state}
      />
      <PreviewStep
        task={task}
        step={next}
        completedPath={completedPath}
        state={state}
      />
      <EmptyStepsMessage isEmpty={isEmpty} />
    </StyledPreviewStepDiv>
  );
}

function EmptyStepsMessage({isEmpty}: {isEmpty: boolean}) {
  return (
    <span className={cs('mx-auto text-muted', {'d-none': !isEmpty})}>
      {gt('No steps for this task.')}
    </span>
  );
}

interface PreviewProps {
  step: NodeType | null;
  completedPath: Array<string>;
  task: SlimTaskInstanceSerializedType;
  state: TaskState;
}

function PreviewStep({task, step, completedPath, state}: PreviewProps) {
  const isCompleted = completedPath.includes(step?.body?.id ?? '');
  const userTimezone = useSelector(
    (state) => CurrentUserSelector(state).data!.timezone
  );
  if (step == null || step.body == null) {
    return null;
  }

  const progress = state.progressMap[step.body.id];

  return (
    <div
      className={cs('mb-2 d-flex align-items-center', {completed: isCompleted})}
    >
      <StyledNumberContainerDiv>
        <ProcedureStepNumber n={step.body.n} type={step.body.type} />
      </StyledNumberContainerDiv>
      <div className="title mr-auto">
        <h2 className="text-truncate">
          <Link to={`${task.html_url}#${step.body.id}`}>
            {getStepName(step.body)}
          </Link>
        </h2>
      </div>
      <TaskStepActionButtons
        isCompleted={isCompleted}
        disabled={true}
        isProcedureStepsBlocked={state.progressInfo.isSequenced}
        completedBy={progress?.completed?.by ?? null}
        completedAt={progress?.completed?.at ?? null}
        userTimezone={userTimezone}
        onToggleCheck={() => Promise.resolve(true)}
      />
    </div>
  );
}

function LoadingText() {
  return (
    <span className="p-3 pt-0 d-block">
      <i className="fa fa-circle-notch fa-spin" /> {gt('loading...')}
    </span>
  );
}

const StyledPreviewStepDiv = styled.div`
  h2 {
    font-size: 1rem !important;
    margin-bottom: 0px;
    margin-right: auto;
    a {
      color: ${colors.text};
    }
  }
  button {
    padding-right: 0 !important;
  }
  .content-type {
    min-height: auto !important;
    display: flex;
    align-items: center;
    justify-content: between;
  }

  .completed {
    opacity: 0.7;
    h2 {
      text-decoration: line-through;
    }
  }

  .completed-by {
    color: ${colors.light};
    &:hover {
      color: ${colors.darkGray};
      transition: color 0.3s ease-in-out;
    }
  }

  .step-n {
    position: static !important;
    margin-right: 0.5rem;
  }

  .title {
    max-width: 360px;
  }
`;

const StyledPopoverForm = styled(PopoverForm)`
  .popover {
    max-width: 500px !important;
    width: 90%;
  }

  .bg-very-light {
    background-color: ${colors.lightBg};
  }

  #hide_comments {
    display: none;
  }
`;
